import { isAxiosError } from 'axios';

const isBadGatewayFromAxios = (err: unknown): boolean =>
  isAxiosError(err) && err.response?.status === 502;

/**
 * Repeats {@link request} with decreasing page size when the server returns HTTP 502
 * (e.g. API Gateway payload limit). Starts at {@link initialPageSize} and halves until
 * success or {@link minPageSize} is reached.
 */
export async function requestWithBisectedPageSize<T>({
  initialPageSize,
  minPageSize = 1,
  request,
}: {
  initialPageSize: number;
  minPageSize?: number;
  request: (pageSize: number) => Promise<T>;
}): Promise<{ data: T; pageSize: number }> {
  let pageSize = initialPageSize;

  while (pageSize >= minPageSize) {
    try {
      const data = await request(pageSize);
      return { data, pageSize };
    } catch (err) {
      if (!isBadGatewayFromAxios(err) || pageSize <= minPageSize) {
        throw err;
      }
      pageSize = Math.max(minPageSize, Math.floor(pageSize / 2));
    }
  }

  throw new Error('requestWithBisectedPageSize: exhausted page sizes');
}
